import React, { useState } from 'react';
import { Lightbulb, Database, Users } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { Spinoff, Staff, Project, IPEntity } from '../types';

interface SpinoffModalProps {
  spinoff?: Spinoff;
  allStaff: Staff[];
  allProjects: Project[];
  allIP: IPEntity[];
  onClose: () => void;
  onSave: () => void;
  isNew?: boolean;
}

export function SpinoffModal({ 
  spinoff, 
  allStaff, 
  allProjects, 
  allIP, 
  onClose, 
  onSave, 
  isNew = false 
}: SpinoffModalProps) {
  const [editedSpinoff, setEditedSpinoff] = useState<Partial<Spinoff>>(
    spinoff || {
      name: '',
      description: '',
      year: new Date().getFullYear().toString(),
      revenue: '',
      ip: [],
      projects: [],
      staff: []
    }
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const years = Array.from(
    { length: new Date().getFullYear() - 2017 },
    (_, i) => (new Date().getFullYear() - i).toString()
  );

  const formatRevenue = (value: string) => {
    const numericValue = value.replace(/[^0-9]/g, '');
    if (numericValue) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(parseInt(numericValue, 10));
    }
    return '';
  };

  const handleSave = async () => {
    setLoading(true);
    setError(null);

    try {
      if (!editedSpinoff.name?.trim()) {
        throw new Error('Name is required');
      }

      if (isNew) {
        const { data: newSpinoff, error: createError } = await supabase
          .from('spinoffs')
          .insert({
            name: editedSpinoff.name,
            description: editedSpinoff.description,
            year: editedSpinoff.year,
            revenue: editedSpinoff.revenue
          })
          .select()
          .single();

        if (createError) throw createError;
        if (!newSpinoff) throw new Error('Failed to create spinoff');

        // Create IP relationships
        if (editedSpinoff.ip?.length) {
          const { error: ipError } = await supabase
            .from('spinoff_ip')
            .insert(
              editedSpinoff.ip.map(ip => ({
                spinoff_id: newSpinoff.id,
                ip_id: ip.id
              }))
            );

          if (ipError) throw ipError;
        }

        // Create project relationships
        if (editedSpinoff.projects?.length) {
          const { error: projectError } = await supabase
            .from('spinoff_projects')
            .insert(
              editedSpinoff.projects.map(project => ({
                spinoff_id: newSpinoff.id,
                project_id: project.id
              }))
            );

          if (projectError) throw projectError;
        }

        // Create staff relationships
        if (editedSpinoff.staff?.length) {
          const { error: staffError } = await supabase
            .from('spinoff_staff')
            .insert(
              editedSpinoff.staff.map(member => ({
                spinoff_id: newSpinoff.id,
                staff_id: member.id
              }))
            );

          if (staffError) throw staffError;
        }
      } else if (spinoff?.id) {
        const { error: updateError } = await supabase
          .from('spinoffs')
          .update({
            name: editedSpinoff.name,
            description: editedSpinoff.description,
            year: editedSpinoff.year,
            revenue: editedSpinoff.revenue
          })
          .eq('id', spinoff.id);

        if (updateError) throw updateError;

        // Update IP relationships
        await supabase
          .from('spinoff_ip')
          .delete()
          .eq('spinoff_id', spinoff.id);

        if (editedSpinoff.ip?.length) {
          const { error: ipError } = await supabase
            .from('spinoff_ip')
            .insert(
              editedSpinoff.ip.map(ip => ({
                spinoff_id: spinoff.id,
                ip_id: ip.id
              }))
            );

          if (ipError) throw ipError;
        }

        // Update project relationships
        await supabase
          .from('spinoff_projects')
          .delete()
          .eq('spinoff_id', spinoff.id);

        if (editedSpinoff.projects?.length) {
          const { error: projectError } = await supabase
            .from('spinoff_projects')
            .insert(
              editedSpinoff.projects.map(project => ({
                spinoff_id: spinoff.id,
                project_id: project.id
              }))
            );

          if (projectError) throw projectError;
        }

        // Update staff relationships
        await supabase
          .from('spinoff_staff')
          .delete()
          .eq('spinoff_id', spinoff.id);

        if (editedSpinoff.staff?.length) {
          const { error: staffError } = await supabase
            .from('spinoff_staff')
            .insert(
              editedSpinoff.staff.map(member => ({
                spinoff_id: spinoff.id,
                staff_id: member.id
              }))
            );

          if (staffError) throw staffError;
        }
      }

      onSave();
    } catch (err) {
      console.error('Error saving spinoff:', err);
      setError(err instanceof Error ? err.message : 'An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const toggleIP = (ip: IPEntity) => {
    const hasIP = editedSpinoff.ip?.some(i => i.id === ip.id);
    if (hasIP) {
      setEditedSpinoff({
        ...editedSpinoff,
        ip: editedSpinoff.ip?.filter(i => i.id !== ip.id) || []
      });
    } else {
      setEditedSpinoff({
        ...editedSpinoff,
        ip: [...(editedSpinoff.ip || []), ip]
      });
    }
  };

  const toggleProject = (project: Project) => {
    const hasProject = editedSpinoff.projects?.some(p => p.id === project.id);
    if (hasProject) {
      setEditedSpinoff({
        ...editedSpinoff,
        projects: editedSpinoff.projects?.filter(p => p.id !== project.id) || []
      });
    } else {
      setEditedSpinoff({
        ...editedSpinoff,
        projects: [...(editedSpinoff.projects || []), project]
      });
    }
  };

  const toggleStaff = (member: Staff) => {
    const hasStaff = editedSpinoff.staff?.some(s => s.id === member.id);
    if (hasStaff) {
      setEditedSpinoff({
        ...editedSpinoff,
        staff: editedSpinoff.staff?.filter(s => s.id !== member.id) || []
      });
    } else {
      setEditedSpinoff({
        ...editedSpinoff,
        staff: [...(editedSpinoff.staff || []), member]
      });
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">
            {isNew ? 'New Spin-off' : 'Edit Spin-off'}
          </h2>
          {error && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}
        </div>

        <div className="p-6 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 200px)' }}>
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Name
              </label>
              <input
                type="text"
                value={editedSpinoff.name}
                onChange={(e) => setEditedSpinoff({ ...editedSpinoff, name: e.target.value })}
                className="w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="Enter spin-off name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                value={editedSpinoff.description}
                onChange={(e) => setEditedSpinoff({ ...editedSpinoff, description: e.target.value })}
                rows={4}
                className="w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="Enter spin-off description"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Year Started
                </label>
                <select
                  value={editedSpinoff.year || ''}
                  onChange={(e) => setEditedSpinoff({ ...editedSpinoff, year: e.target.value })}
                  className="w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="">Select Year</option>
                  {years.map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Revenue
                </label>
                <input
                  type="text"
                  value={editedSpinoff.revenue}
                  onChange={(e) => setEditedSpinoff({
                    ...editedSpinoff,
                    revenue: formatRevenue(e.target.value)
                  })}
                  className="w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Enter revenue"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Intellectual Property
              </label>
              <div className="flex flex-wrap gap-2">
                {allIP.map((ip) => {
                  const isSelected = editedSpinoff.ip?.some(i => i.id === ip.id);
                  return (
                    <button
                      key={ip.id}
                      onClick={() => toggleIP(ip)}
                      className={`inline-flex items-center px-3 py-1 rounded-full text-sm transition-colors ${
                        isSelected
                          ? 'bg-purple-100 text-purple-800'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      <Lightbulb className="w-4 h-4 mr-1" />
                      {ip.title}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Projects
              </label>
              <div className="flex flex-wrap gap-2">
                {allProjects.map((project) => {
                  const isSelected = editedSpinoff.projects?.some(p => p.id === project.id);
                  return (
                    <button
                      key={project.id}
                      onClick={() => toggleProject(project)}
                      className={`inline-flex items-center px-3 py-1 rounded-full text-sm transition-colors ${
                        isSelected
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      <Database className="w-4 h-4 mr-1" />
                      <span className="font-medium">{project.projectName}</span>
                      {project.customer && (
                        <span className="ml-1 text-xs opacity-75">
                          • {project.customer.name}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Team
              </label>
              <div className="flex flex-wrap gap-2">
                {allStaff.map((member) => {
                  const isSelected = editedSpinoff.staff?.some(s => s.id === member.id);
                  return (
                    <button
                      key={member.id}
                      onClick={() => toggleStaff(member)}
                      className={`inline-flex items-center px-3 py-1 rounded-full text-sm transition-colors ${
                        isSelected
                          ? 'bg-indigo-100 text-indigo-800'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      <Users className="w-4 h-4 mr-1" />
                      {member.name}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        <div className="p-6 border-t border-gray-200 bg-gray-50">
          <div className="flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={loading || !editedSpinoff.name?.trim()}
              className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
            >
              {loading ? 'Saving...' : isNew ? 'Create Spin-off' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}